// src/pages/shop/ShopKitchen.jsx
//
// Кухня (KDS). Очередь открытых заказов. По каждому блюду повар жмёт
// «Начать» → «Готово». Запрос на удаление от официанта повар подтверждает
// («Убрать») или отклоняет («Оставить») — тот самый хендшейк.
// Живое обновление: поллинг раз в несколько секунд + рефреш при фокусе.

import { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { cafeOrders as cafeApi } from '../../api';
import { useToast } from '../../hooks/useToast';
import { useReloadOnFocus } from '../../hooks/useReloadOnFocus';
import { errorMessage } from '../../utils/errorMessage';
import Icon from '../../components/Icon';
import BottomNav from '../../components/BottomNav';
import { useShopNav } from './ShopHome';

const POLL_MS = 5000;

function elapsed(t, iso) {
  const min = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (min < 1) return t('kitchen.justNow');
  return t('kitchen.minAgo', { min });
}

export default function ShopKitchen() {
  const { t } = useTranslation();
  const { show, ToastView } = useToast();
  const navItems = useShopNav();

  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const firstLoad = useRef(true);

  const load = useCallback(async () => {
    try {
      const r = await cafeApi.list('open');
      setOrders(r.items || []);
    } catch (e) {
      if (firstLoad.current) show(errorMessage(e, t), 'error');
    } finally {
      firstLoad.current = false;
      setLoading(false);
    }
  }, []); // eslint-disable-line

  useEffect(() => {
    load();
    const id = setInterval(load, POLL_MS);
    return () => clearInterval(id);
  }, [load]);
  useReloadOnFocus(load);

  const act = async (fn) => {
    setBusy(true);
    try { await fn(); await load(); }
    catch (e) { show(errorMessage(e, t), 'error'); }
    finally { setBusy(false); }
  };
  const cook = (orderId, itemId, status) => act(() => cafeApi.cook(orderId, itemId, status));
  const resolve = (orderId, itemId, approve) => act(() => cafeApi.resolveRemoval(orderId, itemId, approve));

  // Заказы, где есть что готовить или что решать — вперёд; полностью готовые — вниз.
  const sorted = [...orders].sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));

  return (
    <div className="page" style={{ paddingBottom: 90 }}>
      {ToastView}
      <div className="page-header">
        <h1 className="page-title">{t('modules.kitchen')}</h1>
      </div>

      <div style={{ padding: '0 20px' }}>
        {loading ? (
          <div style={{ padding: 40, textAlign: 'center' }}><div className="spinner" /></div>
        ) : sorted.length === 0 ? (
          <div style={{ padding: 32, textAlign: 'center', color: 'var(--fg-faint)', fontSize: 13 }}>{t('kitchen.empty')}</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {sorted.map(o => (
              <div key={o.id} className="card" style={{ padding: '12px 14px' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                  <span style={{ fontSize: 15, fontWeight: 800 }}>{t('waiter.tableShort')} {o.tableLabel} · №{o.number}</span>
                  <span style={{ fontSize: 12, color: 'var(--fg-faint)' }}>{elapsed(t, o.createdAt)}</span>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {o.items.map(it => (
                    <div key={it.id} style={{
                      display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', borderRadius: 'var(--r-sm)',
                      background: it.removalRequested ? 'rgba(220,50,50,.08)' : 'var(--bg-subtle)',
                    }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <span style={{ fontSize: 14, fontWeight: 700 }}>{it.qty}× {it.name}</span>
                      </div>

                      {it.removalRequested ? (
                        <>
                          <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--danger)', marginRight: 4 }}>{t('kitchen.removalAsk')}</span>
                          <button className="btn btn-sm" onClick={() => resolve(o.id, it.id, true)} disabled={busy}
                            style={{ width: 'auto', flexShrink: 0, background: 'var(--danger)', color: '#fff' }}>{t('kitchen.removeYes')}</button>
                          <button className="btn btn-sm btn-secondary" onClick={() => resolve(o.id, it.id, false)} disabled={busy}
                            style={{ width: 'auto', flexShrink: 0 }}>{t('kitchen.removeNo')}</button>
                        </>
                      ) : it.cookStatus === 'pending' ? (
                        <button className="btn btn-sm btn-primary" onClick={() => cook(o.id, it.id, 'cooking')} disabled={busy}
                          style={{ width: 'auto', flexShrink: 0 }}>{t('kitchen.start')}</button>
                      ) : it.cookStatus === 'cooking' ? (
                        <button className="btn btn-sm" onClick={() => cook(o.id, it.id, 'done')} disabled={busy}
                          style={{ width: 'auto', flexShrink: 0, background: 'var(--success)', color: '#fff' }}>{t('kitchen.done')}</button>
                      ) : (
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12, fontWeight: 700, color: 'var(--success)', flexShrink: 0 }}>
                          <Icon name="check" size={14} /> {t('waiter.stReady')}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <BottomNav items={navItems} />
    </div>
  );
}
